import { useEffect } from 'react';

import TooltipWrapper from 'approot/shared/tooltip/tooltip-wrapper';
import { Box } from 'approot/shared/design-system/box/box.style';
import { useTeacherOrStudentClassSettings } from '../present/activity-present-settings.hooks';
import { useGetInteractiveLessonParams } from '../activity-builder.hooks';
import { useTTSAudio } from './use-tts-audio';
import TTSListenButton from './controls/tts-listen-button';
import TTSStartAgainButton from './controls/tts-start-again-button';
import TTSPlayPauseButton from './controls/tts-play-pause-button';
import TTSCloseButton from './controls/tts-close-button';
import { ttsPushDataTrackingEvent } from './logic/tts-push-data-tracking-event';

type Props = {
  componentId: string;
  componentType: string;
};

const TTSMenuItem = ({ componentId, componentType }: Props) => {
  const { version, lang, contentType, contentId, isPublic } =
    useGetInteractiveLessonParams();

  const {
    isAudioPlaying,
    isAudioPaused,
    isInPlayMode,
    loadingAudio,
    containerRef,
    playAudio,
    pauseAudio,
    restartAudio,
    stopAudio,
    setIsInPlayMode,
    handlePlayAudio,
    tearDown,
  } = useTTSAudio({ componentId, version, lang, contentType });

  const { ttsEnabled } = useTeacherOrStudentClassSettings();

  useEffect(() => {
    return () => tearDown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if ((!isPublic && !ttsEnabled) || !version) {
    return null;
  }

  return (
    <Box
      aria-label="Text to speech controls"
      role="group"
      ref={containerRef}
      tabIndex={-1}
      $gap={8}
      $alignItems="center"
      $marginBottom={0}
      data-selector="btn-tts-controls"
    >
      {!isInPlayMode || loadingAudio ? (
        <TooltipWrapper hoverText="Listen to text">
          <TTSListenButton
            onClick={() => {
              handlePlayAudio();
              ttsPushDataTrackingEvent({
                componentId,
                componentType,
                contentId,
                contentType,
                lang,
              });
            }}
            loadingAudio={loadingAudio}
          />
        </TooltipWrapper>
      ) : (
        <>
          <TTSPlayPauseButton
            isAudioPlaying={isAudioPlaying}
            isAudioPaused={isAudioPaused}
            onClick={() => {
              if (isAudioPlaying) {
                pauseAudio();
              } else {
                playAudio(componentId);
                ttsPushDataTrackingEvent({
                  componentId,
                  componentType,
                  contentId,
                  contentType,
                  lang,
                });
              }
            }}
          />
          <TTSStartAgainButton
            onClick={() => {
              restartAudio(componentId);
              ttsPushDataTrackingEvent({
                componentId,
                componentType,
                contentId,
                contentType,
                lang,
              });
            }}
          />
          <TTSCloseButton
            onClick={() => {
              stopAudio(componentId);
              setIsInPlayMode(false);
              containerRef.current?.focus();
            }}
          />
        </>
      )}
    </Box>
  );
};

export default TTSMenuItem;
